import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import Sidebar from '../components/Sidebar/Sidebar'
import MonacoEditor from '../components/CodeEditor/MonacoEditor'
import Terminal from '../components/Terminal/Terminal'
import Preview from '../components/Preview/Preview'
import EditorToolbar from '../components/CodeEditor/EditorToolbar'
import { localStorageUtils } from '../hooks/useLocalStorage'
import { codeRunner } from '../utils/codeRunner'
import toast, { Toaster } from 'react-hot-toast'

const EditorPage = () => {
  const [isDark, setIsDark] = useState(false)
  const [currentFile, setCurrentFile] = useState(null)
  const [files, setFiles] = useState({})
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [terminalOpen, setTerminalOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [output, setOutput] = useState('')
  const [terminalOutput, setTerminalOutput] = useState('')

  useEffect(() => {
    const isDarkMode = localStorage.getItem('theme') === 'dark' || 
      window.matchMedia('(prefers-color-scheme: dark)').matches
    setIsDark(isDarkMode)
    
    // Load saved files
    const savedFiles = localStorageUtils.getItem('codeFiles', {})
    setFiles(savedFiles)
    
    // Load last opened file or create default
    const lastFileId = localStorage.getItem('lastOpenedFile')
    if (lastFileId && savedFiles[lastFileId]) {
      setCurrentFile(savedFiles[lastFileId])
    } else if (Object.keys(savedFiles).length > 0) {
      setCurrentFile(savedFiles[Object.keys(savedFiles)[0]])
    } else {
      // Create default file
      const defaultFile = {
        id: 'default',
        name: 'index.js',
        type: 'file',
        language: 'javascript',
        content: `// Welcome to CodeFire! 🔥
// Start coding with auto-suggestions...

function greet(name) {
  return \`Hello, \${name}! Welcome to CodeFire.\`;
}

console.log(greet("Developer"));

// Try typing 'cons' or 'function' to see auto-suggestions
// Press Ctrl+Space for IntelliSense

// Write your code here...
`,
        path: '/',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
      const newFiles = { ...savedFiles, default: defaultFile }
      localStorageUtils.setItem('codeFiles', newFiles)
      setFiles(newFiles)
      setCurrentFile(defaultFile)
    }

    // Listen for run-code event
    const handleRunCode = () => {
      handleRunCodeInternal()
    }

    const handleChangeTheme = (e) => {
      setIsDark(e.detail === 'dark')
    }

    window.addEventListener('run-code', handleRunCode)
    window.addEventListener('change-theme', handleChangeTheme)

    return () => {
      window.removeEventListener('run-code', handleRunCode)
      window.removeEventListener('change-theme', handleChangeTheme)
    }
  }, [])

  const handleFileSelect = (file) => {
    setCurrentFile(file)
    localStorage.setItem('lastOpenedFile', file.id)
  }

  const handleFileCreate = (type, name, path) => {
    const id = Date.now().toString()
    const newFile = {
      id,
      name,
      type,
      language: type === 'file' ? getLanguageFromName(name) : null,
      content: type === 'file' ? getDefaultContent(name) : null,
      path,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
    
    const newFiles = { ...files, [id]: newFile }
    localStorageUtils.setItem('codeFiles', newFiles)
    setFiles(newFiles)
    
    if (type === 'file') {
      setCurrentFile(newFile)
    }
    
    toast.success(`${type === 'file' ? 'File' : 'Folder'} created successfully!`, {
      icon: '📁',
    })
  }

  const handleFileDelete = (fileId) => {
    const newFiles = { ...files }
    delete newFiles[fileId]
    localStorageUtils.setItem('codeFiles', newFiles)
    setFiles(newFiles)
    
    if (currentFile?.id === fileId) {
      const remainingFiles = Object.values(newFiles)
      setCurrentFile(remainingFiles.length > 0 ? remainingFiles[0] : null)
    }
    
    toast.success('File deleted successfully!', {
      icon: '🗑️',
    })
  }

  const handleFileUpdate = (updatedFile) => {
    const newFiles = { ...files, [updatedFile.id]: updatedFile }
    localStorageUtils.setItem('codeFiles', newFiles)
    setFiles(newFiles)
    setCurrentFile(updatedFile)
  }

  const handleRunCodeInternal = async () => {
    if (!currentFile) {
      toast.error('No file to run')
      return
    }
    
    setTerminalOpen(true)
    setTerminalOutput(prev => prev + '$ Running code... ⚡\n')
    
    try {
      const result = await codeRunner.runCode(
        currentFile.language || 'javascript',
        currentFile.content || ''
      )
      
      if (result.success) {
        setTerminalOutput(prev => prev + result.output + '\n')
        if (result.errors) {
          setTerminalOutput(prev => prev + 'Errors: ' + result.errors + '\n')
        }
        toast.success('Code executed successfully! ✅')
      } else {
        setTerminalOutput(prev => prev + 'Error: ' + result.errors + '\n')
        toast.error('Execution failed! ❌')
      }
    } catch (error) {
      setTerminalOutput(prev => prev + `Error: ${error.message}\n`)
      toast.error('Execution error! ❌')
    }
  }

  const handleTerminalClear = () => {
    setTerminalOutput('')
  }

  const handleTerminalClose = () => {
    setTerminalOpen(false)
  }

  const getLanguageFromName = (filename) => {
    const ext = filename.split('.').pop().toLowerCase()
    const languageMap = {
      'js': 'javascript',
      'jsx': 'javascript',
      'ts': 'typescript',
      'tsx': 'typescript',
      'py': 'python',
      'html': 'html',
      'css': 'css',
      'json': 'json',
      'java': 'java',
      'cpp': 'cpp',
      'c': 'cpp',
      'php': 'php',
      'rb': 'ruby',
      'go': 'go',
      'rs': 'rust'
    }
    return languageMap[ext] || 'text'
  }

  const getDefaultContent = (filename) => {
    const ext = filename.split('.').pop().toLowerCase()
    const defaults = {
      'js': `// ${filename}
// JavaScript file with auto-suggestions

function main() {
  console.log("🔥 Hello from ${filename}!");
  
  // Try typing 'cons' or 'function' to see auto-suggestions
  // Press Ctrl+Space for IntelliSense
  
  return "Ready to code! 🔥";
}

main();`,
      'html': `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${filename} - CodeFire</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            margin: 40px;
            background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
            color: #7f1d1d;
        }
        
        .container {
            max-width: 800px;
            margin: 0 auto;
            padding: 30px;
            background: white;
            border-radius: 15px;
            box-shadow: 0 10px 30px rgba(239, 68, 68, 0.1);
            border: 2px solid #fecaca;
        }
        
        h1 {
            color: #dc2626;
            margin-bottom: 20px;
            border-bottom: 3px solid #f87171;
            padding-bottom: 10px;
        }
        
        .codefire-badge {
            display: inline-block;
            background: linear-gradient(45deg, #ef4444, #dc2626);
            color: white;
            padding: 5px 15px;
            border-radius: 20px;
            font-size: 14px;
            margin-bottom: 20px;
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="codefire-badge">🔥 CodeFire</div>
        <h1>Welcome to ${filename}</h1>
        <p>Start building your HTML page here with beautiful red theme...</p>
    </div>
    
    <script>
        console.log("🔥 HTML page loaded!");
    </script>
</body>
</html>`,
      'css': `/* ${filename} */
/* CSS Stylesheet with Red Theme */

* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

:root {
    --primary: #ef4444;
    --primary-dark: #dc2626;
    --primary-light: #fecaca;
    --background: #fef2f2;
    --text: #7f1d1d;
}

body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
    line-height: 1.6;
    color: var(--text);
    background: var(--background);
}

.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
}

/* Red theme buttons */
.btn-primary {
    background: linear-gradient(45deg, var(--primary), var(--primary-dark));
    color: white;
    padding: 10px 20px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
}

.btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(239, 68, 68, 0.3);
}

/* Add your red theme styles here */`,
      'py': `# ${filename}
# Python script with auto-suggestions

def main():
    print("🔥 Hello from ${filename}!")
    
    # Python auto-suggestions work here
    # Try typing 'print' or 'def' to see suggestions
    
    numbers = [1, 2, 3, 4, 5]
    squares = [n**2 for n in numbers]
    
    print(f"Squares: {squares}")
    return squares

if __name__ == "__main__":
    main()`
    }
    return defaults[ext] || `// ${filename}\n// Start coding here... 🔥`
  }

  return (
    <div className="min-h-screen pt-16 md:pt-20 bg-gradient-to-br from-primary-50 via-white to-red-50 dark:from-gray-900 dark:via-gray-800 dark:to-primary-900/30 transition-colors duration-300">
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: isDark ? '#1f2937' : '#ffffff',
            color: isDark ? '#ffffff' : '#1f2937',
            border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
            borderRadius: '12px',
            fontSize: '14px',
          },
        }}
      />
      
      <div className="flex h-[calc(100vh-64px)] md:h-[calc(100vh-80px)]">
        {/* Sidebar */}
        {sidebarOpen && (
          <motion.div
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            className="absolute md:relative w-full md:w-64 h-full z-40 md:z-auto border-r border-gray-200 dark:border-gray-800 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm"
          >
            <Sidebar
              files={files}
              currentFile={currentFile}
              onFileSelect={handleFileSelect}
              onFileCreate={handleFileCreate}
              onFileDelete={handleFileDelete}
              onToggle={() => setSidebarOpen(false)}
            />
          </motion.div>
        )}
        
        {/* Main Editor Area */}
        <div className="flex-1 flex flex-col">
          {/* Editor Toolbar */}
          <EditorToolbar
            currentFile={currentFile}
            onSave={handleFileUpdate}
            onRun={handleRunCodeInternal}
            onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
            sidebarOpen={sidebarOpen}
            onToggleTerminal={() => setTerminalOpen(!terminalOpen)}
            terminalOpen={terminalOpen}
            onTogglePreview={() => setPreviewOpen(!previewOpen)}
            previewOpen={previewOpen}
          />
          
          {/* Code Editor */}
          <div className="flex-1 flex flex-col md:flex-row">
            <div className={`flex-1 ${previewOpen ? 'md:w-2/3' : 'w-full'}`}>
              {currentFile ? (
                <MonacoEditor
                  key={currentFile.id}
                  file={currentFile}
                  onSave={handleFileUpdate}
                  isDark={isDark}
                />
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
                  <div className="text-center">
                    <div className="text-4xl mb-4 animate-pulse">🔥</div>
                    <p className="text-xl font-semibold mb-2">Select a file to start editing</p>
                    <p className="text-sm">Or create a new file from the sidebar</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Preview Panel */}
            {previewOpen && currentFile?.language === 'html' && (
              <div className="md:w-1/3 w-full border-t md:border-t-0 md:border-l border-gray-200 dark:border-gray-800">
                <Preview content={currentFile.content} />
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Terminal */}
      {terminalOpen && (
        <Terminal 
          output={terminalOutput}
          onClear={handleTerminalClear}
          onRunCode={handleRunCodeInternal}
          onClose={handleTerminalClose}
        />
      )}
    </div>
  )
}

export default EditorPage